import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { Button } from "../utils/components/ui/button";
import { Input } from "../utils/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../utils/components/ui/dialog";
import { useWriteOffPurchaseCreditBills } from "../service/reportsService";

import Loading from "../components/common/LoaderCommon";
import ConfirmPopup from "../components/ConfirmPopup";
import NewVendorDialog from "../components/vendor-management/NewVendorDialog";
import PayVendorBalanceDialog from "../components/vendor-management/PayVendorBalanceDialog";
import PurchaseInvoiceDetailModal from "../components/vendor-management/PurchaseInvoiceDetailModal";
import VendorManagementDetail from "../components/vendor-management/VendorManagementDetail";
import { initialVendors } from "../components/vendor-management/vendorManagementData";
import {
  useGetVendorById,
  useGetVendorPurchases,
  useUpdateVendor,
  useDeleteVendor,
  useDownloadVendorPurchasesExcel,
} from "../service/vendorService";
import {
  NewVendorFormValues,
  Vendor,
  VendorInvoice,
} from "../types/vendorManagement";
import {
  CreateVendorRequest,
  VendorPurchaseResponse,
  VendorResponse,
} from "../types/vendorTypes";

const PURCHASE_PAGE_SIZE = 10;

const mapApiVendorToUiVendor = (vendor: VendorResponse): Vendor => {
  const fallbackVendor = initialVendors.find(
    (item) =>
      item.vendorName === vendor.vendorName ||
      item.phoneNumber === vendor.phoneNumber,
  );

  return {
    id: String(vendor.id),
    vendorName: vendor.vendorName,
    contactPerson: vendor.contactPerson,
    phoneNumber: vendor.phoneNumber,
    emailAddress: vendor.emailAddress,
    vatin: vendor.vatin,
    creditPeriod: vendor.creditPeriod,
    creditLimit: vendor.creditLimit,
    outstanding: vendor.outstanding,
    overdueAmount: vendor.overdueAmount,
    availableCredit: vendor.availableCredit,
    overdueDays: vendor.overdueDays,
    invoices: fallbackVendor?.invoices ?? [],
  };
};

const mapApiPurchaseToUiInvoice = (
  purchase: VendorPurchaseResponse,
): VendorInvoice => ({
  id: String(purchase.id),
  invoiceId: purchase.invoiceId,
  invoiceDate: purchase.invoiceDate,
  amount: purchase.amount,
  paymentDate: purchase.paymentDate,
  amountPaid: purchase.amountPaid,
  balanceAmount: purchase.balanceAmount,
  dueDate: purchase.dueDate,
  overdueDays: purchase.overdueDays,
  status:
    purchase.status === "Opening balance"
      ? "Opening Balance"
      : (purchase.status as VendorInvoice["status"]),
});

function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [debouncedInvoiceSearchTerm] = useDebounce(invoiceSearchTerm, 500);
  const [purchaseStartDate, setPurchaseStartDate] = useState("");
  const [purchaseEndDate, setPurchaseEndDate] = useState("");
  const [purchasePage, setPurchasePage] = useState(0);

  const [selectedInvoice, setSelectedInvoice] = useState<VendorInvoice | null>(
    null,
  );
  const [selectedInvoiceForDetails, setSelectedInvoiceForDetails] =
    useState<VendorInvoice | null>(null);

  const [vendorToEdit, setVendorToEdit] = useState<Vendor | null>(null);
  const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null);

  const [writeOffInvoice, setWriteOffInvoice] = useState<VendorInvoice | null>(null);
  const writeOffMutation = useWriteOffPurchaseCreditBills();

  type WriteOffFormInputs = {
    writeOffDate: string;
    reason: string;
    notes: string;
    amount: number;
  };

  const {
    register: registerWriteOff,
    handleSubmit: handleWriteOffSubmit,
    reset: resetWriteOffForm,
    formState: { errors: writeOffErrors },
  } = useForm<WriteOffFormInputs>({
    defaultValues: {
      writeOffDate: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
      amount: 0,
    },
  });

  const handleOpenWriteOff = (invoice: VendorInvoice) => {
    setWriteOffInvoice(invoice);
    resetWriteOffForm({
      writeOffDate: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
      amount: invoice.balanceAmount || 0,
    });
  };

  const handleCloseWriteOff = () => {
    setWriteOffInvoice(null);
    resetWriteOffForm();
  };

  const onSubmitWriteOff = (data: WriteOffFormInputs) => {
    if (!writeOffInvoice) return;

    writeOffMutation.mutate(
      {
        writeOffDate: data.writeOffDate,
        reason: data.reason,
        notes: data.notes,
        items: [
          {
            purchaseEntryId: Number(writeOffInvoice.id),
            amount: Number(data.amount),
          },
        ],
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["useGetVendorPurchases"],
          });
          await queryClient.invalidateQueries({ queryKey: ["useGetVendorById"] });
          toast.success("Invoice written off successfully.");
          handleCloseWriteOff();
        },
        onError: (error: Error) => {
          toast.error(`Unable to write off: ${error.message}`);
        },
      }
    );
  };

  const {
    data: vendorDetailResponse,
    isLoading: isVendorDetailLoading,
    isError: isVendorDetailError,
  } = useGetVendorById(id);

  const {
    data: purchaseHistoryResponse,
    isLoading: isPurchaseHistoryLoading,
    isError: isPurchaseHistoryError,
  } = useGetVendorPurchases({
    vendorId: id,
    search: debouncedInvoiceSearchTerm,
    startDate: purchaseStartDate || undefined,
    endDate: purchaseEndDate || undefined,
    pageNo: purchasePage,
    pageSize: PURCHASE_PAGE_SIZE,
  });

  const { mutate: updateVendor, isPending: isUpdatingVendor } =
    useUpdateVendor();
  const { mutate: deleteVendor } = useDeleteVendor();
  const { mutate: downloadPurchasesExcel, isPending: isExportingPurchases } = useDownloadVendorPurchasesExcel();

  const handleExportPurchaseExcel = () => {
    if (!id) return;
    downloadPurchasesExcel({
      vendorId: id,
      search: debouncedInvoiceSearchTerm,
      startDate: purchaseStartDate || undefined,
      endDate: purchaseEndDate || undefined,
    }, {
      onSuccess: () => toast.success("Excel downloaded successfully"),
      onError: (err) => toast.error(`Failed to download excel: ${err.message}`)
    });
  };

  const selectedVendor = useMemo(
    () =>
      vendorDetailResponse
        ? mapApiVendorToUiVendor(vendorDetailResponse)
        : null,
    [vendorDetailResponse],
  );

  const purchaseInvoices = useMemo(
    () =>
      (purchaseHistoryResponse?.content ?? []).map(mapApiPurchaseToUiInvoice),
    [purchaseHistoryResponse?.content],
  );

  const handleUpdateVendor = (values: NewVendorFormValues) => {
    if (!vendorToEdit) return;
    const payload: CreateVendorRequest = {
      vendorName: values.vendorName,
      contactPerson: values.contactPerson,
      phoneNumber: values.phoneNumber,
      emailAddress: values.emailAddress,
      vatin: values.vatin,
      creditPeriod: Number(values.creditPeriod) || 0,
      creditLimit: Number(values.creditLimit) || 0,
      openingBalance: Number(values.openingBalance) || 0,
    };

    updateVendor(
      { id: Number(vendorToEdit.id), data: payload },
      {
        onSuccess: async () => {
          setVendorToEdit(null);
          await queryClient.invalidateQueries({
            queryKey: ["useGetAllVendors"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["useGetVendorById", vendorToEdit.id],
          });
          toast.success("Vendor updated successfully");
        },
        onError: (error: Error) => {
          toast.error(`Failed to update vendor: ${error.message}`);
        },
      }
    );
  };

  const handleDeleteVendor = () => {
    if (!vendorToDelete) return;
    deleteVendor(Number(vendorToDelete.id), {
      onSuccess: async () => {
        setVendorToDelete(null);
        await queryClient.invalidateQueries({
          queryKey: ["useGetAllVendors"],
        });
        toast.success("Vendor deleted successfully");
        navigate("/vendor-management");
      },
      onError: (error: Error) => {
        toast.error(`Failed to delete vendor: ${error.message}`);
      },
    });
  };

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(26,94,249,0.12),transparent_30%),linear-gradient(180deg,#FCFDFF_0%,#F4F8FF_100%)] px-1 py-3">
      <div className="mx-auto max-w-350 space-y-6">
        {selectedVendor ? (
          isVendorDetailLoading ? (
            <div className="rounded-3xl border border-[#E8EEF9] bg-white py-20 shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
              <Loading />
            </div>
          ) : isVendorDetailError && /^\d+$/.test(id ?? "") ? (
            <div className="rounded-3xl border border-[#F2D9D9] bg-white px-6 py-10 text-center text-sm text-[#B15454] shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
              Unable to load vendor details right now.
            </div>
          ) : (
            <VendorManagementDetail
              vendor={{ ...selectedVendor, invoices: purchaseInvoices }}
              searchTerm={invoiceSearchTerm}
              onSearchChange={(value) => {
                setInvoiceSearchTerm(value);
                setPurchasePage(0);
              }}
              startDate={purchaseStartDate}
              onStartDateChange={(value) => {
                setPurchaseStartDate(value);
                setPurchasePage(0);
              }}
              endDate={purchaseEndDate}
              onEndDateChange={(value) => {
                setPurchaseEndDate(value);
                setPurchasePage(0);
              }}
              isPurchaseHistoryLoading={isPurchaseHistoryLoading}
              isPurchaseHistoryError={isPurchaseHistoryError}
              purchaseCurrentPage={
                purchaseHistoryResponse?.pageNumber ?? purchasePage
              }
              purchaseTotalPages={purchaseHistoryResponse?.totalPages ?? 0}
              purchaseTotalCount={purchaseHistoryResponse?.totalElements ?? 0}
              onPurchasePageChange={setPurchasePage}
              onBack={() => {
                navigate("/vendor-management");
              }}
              onPayInvoice={setSelectedInvoice}
              onViewInvoiceDetails={setSelectedInvoiceForDetails}
              onEditVendor={() => setVendorToEdit(selectedVendor)}
              onDeleteVendor={() => setVendorToDelete(selectedVendor)}
              onWriteOffInvoice={handleOpenWriteOff}
              onExportExcel={handleExportPurchaseExcel}
              isExportingExcel={isExportingPurchases}
            />
          )
        ) : (
          <div className="rounded-3xl border border-[#E8EEF9] bg-white py-20 shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
            <Loading />
          </div>
        )}
      </div>

      <NewVendorDialog
        open={Boolean(vendorToEdit)}
        onOpenChange={(open) => !open && setVendorToEdit(null)}
        onSubmit={handleUpdateVendor}
        isSubmitting={isUpdatingVendor}
        isEdit
        initialData={
          vendorToEdit
            ? {
                vendorName: vendorToEdit.vendorName,
                contactPerson: vendorToEdit.contactPerson,
                phoneNumber: vendorToEdit.phoneNumber,
                emailAddress: vendorToEdit.emailAddress,
                vatin: vendorToEdit.vatin || "",
                creditPeriod: String(vendorToEdit.creditPeriod),
                creditLimit: String(vendorToEdit.creditLimit),
                openingBalance: "0", // Currently read-only or handled elsewhere
              }
            : undefined
        }
      />
      <ConfirmPopup
        open={Boolean(vendorToDelete)}
        setOpen={(open) => !open && setVendorToDelete(null)}
        onConfirm={handleDeleteVendor}
        title="Delete Vendor"
        message={`Are you sure you want to delete ${vendorToDelete?.vendorName}? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="bg-[#D93A3A]"
      />
      <PayVendorBalanceDialog
        invoice={selectedInvoice}
        open={Boolean(selectedInvoice)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedInvoice(null);
          }
        }}
        onSuccess={() => {
          setSelectedInvoice(null);
          queryClient.invalidateQueries({
            queryKey: ["useGetVendorPurchases"],
          });
          queryClient.invalidateQueries({ queryKey: ["useGetVendorById"] });
          queryClient.invalidateQueries({ queryKey: ["useGetAllVendors"] });
        }}
      />
      <PurchaseInvoiceDetailModal
        vendorId={Number(id)}
        purchaseNumber={selectedInvoiceForDetails?.invoiceId ?? null}
        open={Boolean(selectedInvoiceForDetails)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedInvoiceForDetails(null);
          }
        }}
      />
      <Dialog
        open={!!writeOffInvoice}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseWriteOff();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[520px] rounded-[28px] border border-[#E8EDF3] bg-white p-0 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.45)]"
        >
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-[#1A5EF9]">
                Write Off Invoice
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-slate-500">
                Write off invoice {writeOffInvoice?.invoiceId || ""}. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleWriteOffSubmit(onSubmitWriteOff)} className="mt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Write-off Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  {...registerWriteOff("writeOffDate", { required: "Date is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.writeOffDate && (
                  <p className="text-red-500 text-xs">{writeOffErrors.writeOffDate.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Amount <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  step="0.001"
                  {...registerWriteOff("amount", { required: "Amount is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.amount && (
                  <p className="text-red-500 text-xs">{writeOffErrors.amount.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Reason <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. Uncollectible debt"
                  {...registerWriteOff("reason", { required: "Reason is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.reason && (
                  <p className="text-red-500 text-xs">{writeOffErrors.reason.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Notes
                </label>
                <textarea
                  placeholder="Additional details..."
                  {...registerWriteOff("notes")}
                  className="w-full min-h-[80px] p-3 text-sm rounded-xl border border-[#E6ECF5] bg-[#F8FAFC] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>

              <DialogFooter className="mt-8 border-t border-[#E8EDF3] pt-6 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseWriteOff}
                  className="h-11 min-w-[112px] rounded-xl border-[#D8E1EC] px-6 text-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={writeOffMutation.isPending}
                  className="h-11 min-w-[112px] rounded-xl bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
                >
                  {writeOffMutation.isPending ? "Submitting..." : "Submit"}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VendorDetail;
